/* =============================================================================
   CORE / STATE — defaultState, loadState, currentRestaurantId
   =============================================================================
   - defaultState(): Seed-Daten für Tische, Settings (inkl. Templates), Menü.
   - loadState(): Storage-Init + Defaults-Merge + Bestandsdaten-Migration.
   - currentRestaurantId(): aktuell aktive Restaurant-ID.
   - saveState(): Deprecated — Persistenz läuft per Entity über App.storage.
============================================================================ */

import { uid } from '../utils/helpers.js';

export function currentRestaurantId() {
  // Bevorzugt den zentralen Resolver (F-04, Session 8.3). Der bindet die
  // Tenant-ID an den eingeloggten User (auth.getCurrentRestaurantIdSync) und
  // fällt auf die Config-Default-ID zurück, wenn niemand eingeloggt ist.
  if (window.App && typeof window.App.getCurrentRestaurantId === 'function') {
    return window.App.getCurrentRestaurantId();
  }
  // Defensiver Fallback, falls der Resolver (noch) nicht registriert ist —
  // z.B. wenn dieses Modul ausnahmsweise vor dem Storage-Adapter geladen wird.
  return (window.App && window.App.storage && window.App.storage._getInternalState
          && window.App.storage._getInternalState().restaurantId)
    || (window.AppConfig && window.AppConfig.defaultRestaurantId)
    || 'rest_default';
}

export function defaultState() {
  return {
    tables: [
      { id: 'i1',  name: 'I1',  area: 'indoor',  seats: 2, extendable: true,  extraChairs: 1, disabled: false, x: 30,  y: 30 },
      { id: 'i2',  name: 'I2',  area: 'indoor',  seats: 2, extendable: true,  extraChairs: 1, disabled: false, x: 130, y: 30 },
      { id: 'i3',  name: 'I3',  area: 'indoor',  seats: 2, extendable: true,  extraChairs: 1, disabled: false, x: 230, y: 30 },
      { id: 'i4',  name: 'I4',  area: 'indoor',  seats: 2, extendable: true,  extraChairs: 1, disabled: false, x: 330, y: 30 },
      { id: 'i5',  name: 'I5',  area: 'indoor',  seats: 4, extendable: true,  extraChairs: 1, disabled: false, x: 30,  y: 140 },
      { id: 'i6',  name: 'I6',  area: 'indoor',  seats: 4, extendable: true,  extraChairs: 1, disabled: false, x: 160, y: 140 },
      { id: 'i7',  name: 'I7',  area: 'indoor',  seats: 4, extendable: true,  extraChairs: 1, disabled: false, x: 290, y: 140 },
      { id: 'i8',  name: 'I8',  area: 'indoor',  seats: 4, extendable: true,  extraChairs: 1, disabled: false, x: 420, y: 140 },
      { id: 'i9',  name: 'I9',  area: 'indoor',  seats: 2, extendable: false, extraChairs: 0, disabled: false, x: 30,  y: 260 },
      { id: 'i10', name: 'I10', area: 'indoor',  seats: 2, extendable: false, extraChairs: 0, disabled: false, x: 130, y: 260 },
      { id: 'c1',  name: 'Club',area: 'club',    seats: 12, extendable: false, extraChairs: 2, disabled: false, x: 40,  y: 100 },
      { id: 'o1',  name: 'A1',  area: 'outdoor', seats: 2, extendable: true,  extraChairs: 1, disabled: false, x: 30,  y: 30 },
      { id: 'o2',  name: 'A2',  area: 'outdoor', seats: 2, extendable: true,  extraChairs: 1, disabled: false, x: 130, y: 30 },
      { id: 'o3',  name: 'A3',  area: 'outdoor', seats: 2, extendable: true,  extraChairs: 1, disabled: false, x: 230, y: 30 },
      { id: 'o4',  name: 'A4',  area: 'outdoor', seats: 2, extendable: true,  extraChairs: 1, disabled: false, x: 330, y: 30 },
      { id: 'o5',  name: 'A5',  area: 'outdoor', seats: 2, extendable: false, extraChairs: 0, disabled: false, x: 30,  y: 140 },
      { id: 'o6',  name: 'A6',  area: 'outdoor', seats: 2, extendable: false, extraChairs: 0, disabled: false, x: 130, y: 140 },
      { id: 'o7',  name: 'A7',  area: 'outdoor', seats: 2, extendable: false, extraChairs: 0, disabled: false, x: 230, y: 140 },
      { id: 'o8',  name: 'A8',  area: 'outdoor', seats: 4, extendable: true,  extraChairs: 1, disabled: false, x: 350, y: 140 }
    ],
    reservations: [],
    settings: {
      restaurantName: 'Grill & Green',
      restaurantEmail: 'reservierung@grill-and-green.de',
      restaurantPhone: '+49 ...',
      // Phase 28 (S26): IANA-Timezone für GoBD-konforme Tagesgrenzen
      // (Z-Bon-Datum, DSFinV-K-Tagesabschluss, Reminder-Versand).
      timezone: 'Europe/Berlin',
      manualConfirmThreshold: 7,
      spontaneousReservePercent: 25,
      outdoorEnabled: true,
      clubEnabled: true,
      slotDurationMin: 120,
      openingTimeStart: '11:00',
      openingTimeEnd:   '23:00',
      allowExtraChair: true,
      allowCustomerCancel: true,
      adjacencyThresholdPx: 55,
      confirmEmailTemplate:
`Hallo {name},

vielen Dank für deine Reservierung im {restaurant}!

Wir bestätigen dir hiermit folgenden Tisch:
• Datum: {date}
• Uhrzeit: {time} Uhr
• Personen: {guests}
• Bereich: {area}

{preorder_block}

{notes_block}

Solltest du deinen Termin nicht wahrnehmen können, kannst du hier kostenlos stornieren:
{cancel_link}

Wir freuen uns auf dich!

Herzliche Grüße
Dein {restaurant}-Team
{phone}`,
      reminderEmailTemplate:
`Hallo {name},

kurze Erinnerung: wir freuen uns auf dich morgen am {date} um {time} Uhr für {guests} Personen.

Falls sich deine Pläne geändert haben, kannst du hier stornieren:
{cancel_link}

Bis bald!
Dein {restaurant}-Team
{phone}`,
      declineEmailTemplate:
`Hallo {name},

vielen Dank für deine Reservierungsanfrage für den {date} um {time} Uhr ({guests} Personen).

Leider müssen wir deine Anfrage absagen — der gewünschte Zeitraum ist bei uns bereits ausgebucht. Wir würden uns sehr freuen, wenn du es zu einem anderen Termin nochmal versuchst.

Falls du Fragen hast, melde dich gerne unter {phone}.

Herzliche Grüße
Dein {restaurant}-Team`
    },
    menu: [
      { id: 'm1',  name: 'Classic Beef Burger',       category: 'Burger',     price: 12.50, available: true },
      { id: 'm2',  name: 'Cheese Lover Burger',       category: 'Burger',     price: 13.90, available: true },
      { id: 'm3',  name: 'BBQ Bacon Burger',          category: 'Burger',     price: 14.50, available: true },
      { id: 'm4',  name: 'Green Veggie Burger',       category: 'Burger',     price: 13.20, available: true },
      { id: 'm5',  name: 'Hähnchen Crispy Burger',    category: 'Burger',     price: 13.90, available: true },
      { id: 'm6',  name: 'Sweet Potato Fries',        category: 'Beilagen',   price: 5.50,  available: true },
      { id: 'm7',  name: 'Classic Pommes',            category: 'Beilagen',   price: 4.50,  available: true },
      { id: 'm8',  name: 'Caesar Salat',              category: 'Salate',     price: 9.80,  available: true },
      { id: 'm9',  name: 'Green Bowl',                category: 'Salate',     price: 11.50, available: true },
      { id: 'm10', name: 'Hausgemachte Limonade',     category: 'Getränke',   price: 4.20,  available: true },
      { id: 'm11', name: 'Craft Beer 0,3l',           category: 'Getränke',   price: 4.80,  available: true },
      { id: 'm12', name: 'Brownie mit Vanilleeis',    category: 'Desserts',   price: 6.50,  available: true }
    ]
  };
}

/**
 * `state` ist eine Referenz auf den intern vom Storage-Adapter verwalteten
 * State. Wird global ans `window` gehängt, damit Module und inline-onclicks
 * weiter wie gewohnt darauf zugreifen können.
 */
export let state = null;

export async function loadState() {
  await window.App.storage.init(async (storage) => {
    const def = defaultState();
    await storage.settings.update(def.settings);
    for (const t of def.tables) await storage.tables.create(t);
    for (const m of def.menu) await storage.menu.create(m);
  });
  state = window.App.storage._getInternalState();
  window.state = state; // window-Exposing für Backward-Compat

  const def = defaultState();
  state.settings = Object.assign({}, def.settings, state.settings || {});
  if (!Array.isArray(state.menu)) state.menu = [];
  if (!Array.isArray(state.tables)) state.tables = [];
  if (!Array.isArray(state.reservations)) state.reservations = [];
  if (!Array.isArray(state.users)) state.users = [];
  if (!Array.isArray(state.auditLog)) state.auditLog = [];
  if (!state.restaurantId) state.restaurantId = currentRestaurantId();

  state.tables.forEach(t => {
    if (typeof t.extendable !== 'boolean') t.extendable = true;
    if (!t.restaurantId) t.restaurantId = state.restaurantId;
    if (typeof t.extraChairs !== 'number' || !Number.isFinite(t.extraChairs) || t.extraChairs < 0) {
      const legacyAllow = state.settings && state.settings.allowExtraChair !== false;
      t.extraChairs = legacyAllow ? 1 : 0;
    }
    if (typeof t.disabled !== 'boolean') t.disabled = false;
  });

  const fallbackDuration = (() => {
    const v = Number(state.settings && state.settings.slotDurationMin);
    return Number.isFinite(v) && v > 0 ? v : 120;
  })();
  state.reservations.forEach(r => {
    if (!r.cancelToken) r.cancelToken = uid('c').slice(2);
    if (!r.restaurantId) r.restaurantId = state.restaurantId;
    if (!r.source) r.source = r.source || 'online';
    const d = Number(r.durationMin);
    if (!Number.isFinite(d) || d <= 0) r.durationMin = fallbackDuration;
    else if (typeof r.durationMin !== 'number') r.durationMin = d;
  });

  {
    const v = Number(state.settings.slotDurationMin);
    state.settings.slotDurationMin = Number.isFinite(v) && v > 0 ? v : 120;
  }
  state.menu.forEach(m => { if (!m.restaurantId) m.restaurantId = state.restaurantId; });

  // Settings-Migrationen (legacy openingHour* → openingTime*)
  if (typeof state.settings.openingHourStart === 'number') {
    state.settings.openingTimeStart = String(state.settings.openingHourStart).padStart(2, '0') + ':00';
    delete state.settings.openingHourStart;
  }
  if (typeof state.settings.openingHourEnd === 'number') {
    state.settings.openingTimeEnd = String(state.settings.openingHourEnd).padStart(2, '0') + ':00';
    delete state.settings.openingHourEnd;
  }
  if (typeof state.settings.allowExtraChair !== 'boolean') state.settings.allowExtraChair = true;
  if (typeof state.settings.allowCustomerCancel !== 'boolean') state.settings.allowCustomerCancel = true;
  if (typeof state.settings.allowWaitlist !== 'boolean') state.settings.allowWaitlist = true;
  if (typeof state.settings.requireGdprConsent !== 'boolean') state.settings.requireGdprConsent = true;
  if (typeof state.settings.adjacencyThresholdPx !== 'number') state.settings.adjacencyThresholdPx = 55;
  if (typeof state.settings.brandPrimaryColor !== 'string') state.settings.brandPrimaryColor = '#2f6b3a';
  if (typeof state.settings.brandAccentColor !== 'string')  state.settings.brandAccentColor  = '#d97a2c';
  if (typeof state.settings.gdprPolicyUrl !== 'string')     state.settings.gdprPolicyUrl = '';

  window.App.storage.flush();
}

export function saveState() {
  console.warn('[deprecated] saveState() aufgerufen — bitte App.storage.<entity>.update/create/remove nutzen.', new Error().stack);
}

// Backward-Compat
if (typeof window !== 'undefined') {
  Object.assign(window, {
    defaultState,
    currentRestaurantId,
    loadState,
    saveState
  });
}
